import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircleIcon } from "@heroicons/react/24/outline";

export interface BaseDefinition {
  _id: string;
  version: number;
  isActive: boolean;
  questions: unknown[];
  createdAt?: string;
  displayName?: string | null;
}

interface Props {
  definition: BaseDefinition;
  editPath: string;
  onActivate: () => void;
  activating: boolean;
  onDelete?: () => void;
  deleting?: boolean;
  onRename?: () => void;
  extraMeta?: ReactNode;
}

export function DefinitionRow({
  definition,
  editPath,
  onActivate,
  activating,
  onDelete,
  deleting,
  onRename,
  extraMeta,
}: Props) {
  const navigate = useNavigate();

  const createdDate = definition.createdAt
    ? new Date(definition.createdAt).toLocaleDateString()
    : "—";

  const primaryLabel =
    definition.displayName?.trim() || `v${definition.version}`;
  const versionHint = definition.displayName?.trim()
    ? `v${definition.version}`
    : null;

  return (
    <div
      className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 ${
        definition.isActive
          ? "border-electrum-300 bg-electrum-50"
          : "border-gray-200 bg-white"
      }`}
    >
      <div className="flex items-center gap-2 min-w-0">
        {definition.isActive && (
          <CheckCircleIcon className="w-4 h-4 text-electrum-300 shrink-0" />
        )}
        {onRename ? (
          <button
            type="button"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={onRename}
            title="Rename"
            className="text-left min-w-0 rounded px-1 -mx-1 py-0.5 hover:bg-black/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-electrum-300"
          >
            <span className="text-sm font-semibold text-gray-800">
              {primaryLabel}
            </span>
            {versionHint && (
              <span className="text-xs font-normal text-gray-400 ml-1">
                · {versionHint}
              </span>
            )}
          </button>
        ) : (
          <span className="text-sm font-semibold text-gray-800">
            v{definition.version}
          </span>
        )}
        <span className="text-xs text-gray-400 hidden sm:block">
          {createdDate}
        </span>
        <span className="text-xs text-gray-400 hidden sm:block">
          · {definition.questions.length} question
          {definition.questions.length !== 1 ? "s" : ""}
        </span>
        {extraMeta}
        {definition.isActive && (
          <span className="text-xs font-semibold text-electrum-300 bg-electrum-100 rounded-full px-2 py-0.5">
            Active
          </span>
        )}
      </div>

      <div className="flex items-center gap-1.5 shrink-0">
        <button
          type="button"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={() => navigate(editPath)}
          className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-600 hover:border-electrum-300 hover:text-electrum-300 transition-colors"
        >
          Edit
        </button>
        {!definition.isActive && (
          <>
            <button
              type="button"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={onActivate}
              disabled={activating}
              className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-600 hover:border-electrum-300 hover:text-electrum-300 transition-colors disabled:opacity-50"
            >
              {activating ? "Activating…" : "Activate"}
            </button>
            {onDelete && (
              <button
                type="button"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={onDelete}
                disabled={deleting}
                className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-400 hover:border-salmon-300 hover:text-salmon-400 transition-colors disabled:opacity-50"
              >
                {deleting ? "Deleting…" : "Delete"}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
